import { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import moment from "moment";
import Loader from "../../Component/Loader/Loader";
import AssignPlanModal from "../Users/modals/AssignPlanModal";
import AssignAffiliateModal from "../Users/modals/AssignAffiliateModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import WabaDetailsModal from "./modals/WabaDetailsModal";
import ManageCreditsModal from "./modals/ManageCreditsModal";
import ConfirmActionModal, { ConfirmAction } from "./modals/ConfirmActionModal";
// static design actions (not connected to the backend yet)
import AdjustWalletModal from "./modals/AdjustWalletModal";
import CreditLineModal from "./modals/CreditLineModal";
import ExtendValidityModal from "./modals/ExtendValidityModal";
import SendEmailModal from "./modals/SendEmailModal";
import FeatureOverridesModal from "./modals/FeatureOverridesModal";
import EditCustomerDrawer from "./components/EditCustomerDrawer";


// Interface for Stats
interface StatItem {
  value: number;
  growth: number;
}

interface UserStats {
  totalUsers: StatItem;
  activeUsers: StatItem;
  onboardUsers: StatItem;
  offboardUsers: StatItem;
}

interface RowMenu {
  user: any;
  top: number;
  left: number;
  anchorTop: number;
}

const DATE_FILTERS: [string, string][] = [
  ["thisWeek", "This Week"],
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["thisMonth", "This Month"],
  ["lastMonth", "Last Month"],
  ["allTime", "All Time"],
  ["custom", "Custom"],
];

const STATUS_PILL: Record<number, [string, string]> = {
  1: ["ok", "Active"],
  0: ["warn", "Inactive"],
  2: ["bad", "Trash"],
};

const inr = (n: number) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "Credit" column: the SmartPing credit-line attach result (user_credit_line_attach) when an attach
// has been attempted; otherwise — older users, or not attempted yet — the user_is_credit_line_enabled flag.
// [cx-si tone, icon, tooltip, CSV label]
const creditLineState = (u: any): [string, string, string, string] => {
  const attach = u?.user_credit_line_attach;
  if (attach?.status === "attached") {
    const when = attach.attached_at ? ` · ${moment(attach.attached_at).format("DD MMM YYYY")}` : "";
    const note = attach.error ? ` · ${attach.error}` : ""; // e.g. "WABA ID already mapped"
    return ["ok", "ri-bank-card-2-fill", `Credit line attached${when}${note}`, "Attached"];
  }
  if (attach?.status === "failed") {
    return ["bad", "ri-error-warning-fill", `Credit line attach failed${attach.error ? `: ${attach.error}` : ""}`, "Attach failed"];
  }
  return u?.user_is_credit_line_enabled
    ? ["ok", "ri-bank-card-2-fill", "Credit line enabled", "Yes"]
    : ["none", "ri-subtract-line", "Credit line not enabled", "No"];
};

const UsersNew = () => {
  const [userList, setUserList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearch = searchParams.get("search") || "";

  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAssignAffiliateModal, setShowAssignAffiliateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showWabaModal, setShowWabaModal] = useState(false);
  // static design modals: "wallet" | "credit" | "extend" | "email" | "overrides" | "edit"
  const [staticModal, setStaticModal] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Row action menu
  const [rowMenu, setRowMenu] = useState<RowMenu | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Stats State
  const [stats, setStats] = useState<UserStats>({
    totalUsers: { value: 0, growth: 0 },
    activeUsers: { value: 0, growth: 0 },
    onboardUsers: { value: 0, growth: 0 },
    offboardUsers: { value: 0, growth: 0 }
  });

  // Filters & Bulk Actions
  const [searchText, setSearchText] = useState(urlSearch);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [dateFilter, setDateFilter] = useState("thisWeek");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // a search coming from the Ctrl K palette looks across all customers
  const [viewFilter, setViewFilter] = useState(urlSearch ? "All" : "Onboard");
  const [sortOrder, setSortOrder] = useState("newest");

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [tabCounts, setTabCounts] = useState({ all: 0, active: 0, onboard: 0, inactive: 0, trash: 0, planExpired: 0, nonCredit: 0 });

  // Calculate Dates when Dropdown Changes
  useEffect(() => {
    let start = "";
    let end = "";

    switch (dateFilter) {
      case "today":
        start = moment().startOf('day').format('YYYY-MM-DD');
        end = moment().endOf('day').format('YYYY-MM-DD');
        break;
      case "yesterday":
        start = moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD');
        break;
      case "thisWeek":
        start = moment().startOf('week').format('YYYY-MM-DD');
        end = moment().endOf('week').format('YYYY-MM-DD');
        break;
      case "thisMonth":
        start = moment().startOf('month').format('YYYY-MM-DD');
        end = moment().endOf('month').format('YYYY-MM-DD');
        break;
      case "lastMonth":
        start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case "custom":
        // keep the range that was showing; the date inputs change it from here
        return;
      case "allTime":
      default:
        start = "";
        end = "";
    }

    setStartDate(start);
    setEndDate(end);
  }, [dateFilter]);

  // Fetch User List
  const getUserList = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/user/userList?page=${page}&limit=${pageSize}&activeTab=${viewFilter}&search=${searchQuery}&sort=${sortOrder}`
      );

      if (response.status === 'success') {
        setUserList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalUsers(response.pagination.total);
        if (response.tabCounts) {
          setTabCounts(response.tabCounts);
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading users!");
    } finally {
      setIsLoader(false);
      setSelectedUsers([]);
    }
  }, [page, pageSize, viewFilter, searchQuery, sortOrder]);

  // Fetch Stats
  const fetchStats = useCallback(async () => {
    if (dateFilter !== "allTime" && (!startDate || !endDate)) return;
    try {
      const response = await getRequest(`/user/userStats?startDate=${startDate}&endDate=${endDate}`);
      if (response.status === 'success' && response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to load user stats.");
    }
  }, [startDate, endDate, dateFilter]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Palette search while already on this page (component does not remount)
  useEffect(() => {
    if (urlSearch) {
      setSearchText(urlSearch);
      setViewFilter("All");
    }
  }, [urlSearch]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Close the row menu on outside click / scroll / resize
  useEffect(() => {
    if (!rowMenu) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setRowMenu(null);
    };
    // scrolling inside the (scrollable) menu itself must not close it
    const close = (e?: Event) => {
      if (e && e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      setRowMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [rowMenu]);

  // Flip the row menu above the button when it would overflow the viewport
  useLayoutEffect(() => {
    if (!rowMenu || !menuRef.current) return;
    const mh = menuRef.current.offsetHeight;
    if (rowMenu.top + mh > window.innerHeight - 8) {
      const top = Math.max(8, rowMenu.anchorTop - mh - 4);
      if (top !== rowMenu.top) setRowMenu({ ...rowMenu, top });
    }
  }, [rowMenu]);

  const openRowMenu = (e: React.MouseEvent<HTMLButtonElement>, user: any) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setRowMenu({ user, top: r.bottom + 4, left: Math.max(8, r.right - 210), anchorTop: r.top });
  };

  const handleDirectLogin = async (userId: string, isCreditLine: boolean) => {
    try {
      const response = await postRequest(`/user/admin-login-as-user`, { user_id: userId });
      if (response.status === 'success') {
        toast.success("Opening user dashboard...");
        localStorage.setItem("temp_impersonate_data", JSON.stringify(response.data));
        if (isCreditLine) {
          window.open(`https://crm.uomox.com/auto-login?token=${response.token}`, '_blank');
        } else {
          window.open(`https://app.uomox.com/auto-login?token=${response.token}`, '_blank');
        }
      } else {
        toast.error(response.message || "Failed to login as this user");
      }
    } catch (error) {
      toast.error("Something went wrong during direct login.");
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUsers(userList.map(u => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (id: string) => {
    setSelectedUsers(prev =>
      prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
    );
  };

  // bulkAction codes: 1 Active, 2 Inactive, 3 Move to trash, 5 Restore, 6 Permanently delete
  // also used by the row ⋮ menu with a single id (already confirmed there)
  const handleApplyBulkAction = async (bulkAction: string, ids: string[] = selectedUsers, confirmed = false) => {
    if (ids.length === 0) return toast.warning("Please select at least one user");
    if (bulkAction === "6" && !confirmed && !window.confirm(`Permanently delete ${ids.length} customer(s)? This cannot be undone.`)) return;

    try {
      setIsLoader(true);
      const response = await postRequest(`/user/handleBulkAction`, {
        bulkAction: bulkAction,
        selectedUsers: ids
      });

      if (response.status === 'success') {
        toast.success(ids.length === 1 && ids !== selectedUsers ? "Customer updated" : "Bulk action applied successfully");
        getUserList();
        fetchStats();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error applying bulk action");
    } finally {
      setIsLoader(false);
    }
  };

  const isSubscriptionExpired = (dateString: string) => {
    if (!dateString) return false;
    return moment(dateString).isBefore(moment(), 'day');
  };

  const handleOpenModal = (user: any) => {
    setSelectedUserId(user);
    setShowWabaModal(true);
  };

  const handleReconcileWallet = async (userId: string) => {
    const isConfirmed = window.confirm("Are you sure you want to reconcile this user's wallet ledger?");
    if (!isConfirmed) return;

    setIsLoader(true);
    try {
      const response = await postRequest(`/user/reconcile-wallet`, { userId: userId });
      if (response.status === 'success' || response.success) {
        toast.success(response.message || "Wallet reconciled successfully!");
        getUserList();
      } else {
        toast.error(response.message || "Failed to reconcile wallet.");
      }
    } catch (error: any) {
      toast.error("Something went wrong while reconciling the wallet.");
    } finally {
      setIsLoader(false);
    }
  };

  // "+91 9876543210" — the "+" and space keep Excel from turning long numbers into 9.18E+11
  const formatPhone = (code?: string, mobile?: string) => {
    const m = String(mobile || "").trim();
    if (!m) return "";
    const c = String(code || "").replace(/\D/g, "");
    return c ? `+${c} ${m}` : m;
  };
  const formatWaNumber = (n?: string) => {
    const v = String(n || "").trim();
    return v && !v.startsWith("+") ? `+${v}` : v;
  };

  // Export every customer in the selected tab + search (all pages, same sort) as CSV
  // Old version exported only the rows loaded on the current page:
  // const exportCustomers = () => { if (!userList.length) return ...; const rows = userList.map(...); ... };
  const exportCustomers = async () => {
    if (!totalUsers) return toast.warning("Nothing to export");
    setIsLoader(true);
    try {
      const response = await getRequest(
        `/user/exportUsers?activeTab=${viewFilter}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}`
      );
      if (response.status === 'session_expired') return logout();
      if (response.status !== 'success') return toast.error(response.message || "Couldn't export customers");
      const list: any[] = response.data || [];
      if (!list.length) return toast.warning("Nothing to export");

      const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const head = ["ID", "First name", "Last name", "Email", "Phone", "Company", "Plan", "Status", "WABA connected", "WhatsApp number", "Credit line", "Wallet", "Free msgs used", "Renewal", "Affiliate", "Joined"];
      const rows = list.map(u => [
        u.user_unique_id, u.user_first_name, u.user_last_name, u.user_email, formatPhone(u.user_country_code, u.user_mobile),
        u.user_companyName, u.user_plan_name,
        STATUS_PILL[u.user_status]?.[1] || "", u.user_whatsapp?.isWabaConnected ? "Yes" : "No",
        formatWaNumber(u.user_meta_analytics?.display_phone_number),
        creditLineState(u)[3], u.user_is_credit_line_enabled ? Number(u.user_wallet_balance || 0).toFixed(2) : "",
        u.serviceMessageUsage ? `${u.serviceMessageUsage.count} / ${u.serviceMessageUsage.limit}` : "",
        u.user_plan_end_date ? moment(u.user_plan_end_date).format("DD MMM YYYY") : "",
        u.user_affiliate_id && typeof u.user_affiliate_id === "object"
          ? `${u.user_affiliate_id.affiliate_first_name || ""} ${u.user_affiliate_id.affiliate_last_name || ""}`.trim()
          : "",
        u.createdAt ? moment(u.createdAt).format("DD MMM YYYY") : "",
      ]);
      // BOM so Excel reads names with accents / Indian scripts correctly
      const csv = "﻿" + [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const tabSlug = viewFilter.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase(); // PlanExpired → plan-expired
      a.download = `customers-${tabSlug}${searchQuery ? "-search" : ""}-${moment().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${list.length.toLocaleString("en-IN")} customers`);
    } catch (error) {
      toast.error("Something went wrong exporting customers");
    } finally {
      setIsLoader(false);
    }
  };

  const changeView = (v: string) => { setViewFilter(v); setPage(1); };

  const resetFilters = () => {
    setSearchText("");
    setSortOrder("newest");
    changeView("All");
  };

  // Switching to Custom from All Time starts from this month
  const handleDateFilterChange = (value: string) => {
    if (value === "custom" && (!startDate || !endDate)) {
      setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
      setEndDate(moment().endOf('month').format('YYYY-MM-DD'));
    }
    setDateFilter(value);
  };

  const displayDateRange = (startDate && endDate)
    ? `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`
    : "All Time";

  // Helper for rendering initials
  const getInitials = (first: string, last: string) => {
    return `${(first || '').charAt(0)}${(last || '').charAt(1)}`.toUpperCase() || 'U';
  };

  // One stat cell: label, value, icon, growth % (invert = a rise is bad)
  const StatCell = ({ label, value, icon, growth, invert }: { label: string; value: number | string; icon: string; growth?: number; invert?: boolean }) => {
    const hasGrowth = typeof growth === "number";
    const good = hasGrowth && ((growth! >= 0) !== !!invert);
    return (
      <div className="cx-st-c">
        <div className="cx-st-h"><span>{label}</span><i className={icon}></i></div>
        <div className="cx-st-v">{typeof value === "number" ? value.toLocaleString("en-IN") : value}</div>
        <div className="cx-st-f">
          {hasGrowth ? (
            <>
              <span className={`cx-st-d ${growth === 0 ? "" : good ? "g" : "b"}`}>
                <i className={growth! >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line"}></i>{Math.abs(growth!).toFixed(1)}%
              </span>
              <span>vs prev. period</span>
            </>
          ) : <span>Not tracked yet</span>}
        </div>
      </div>
    );
  };

  // Page numbers with ellipsis: 1 … 4 5 6 … 20
  const pageItems = (): (number | string)[] => {
    const out: (number | string)[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== "…") out.push("…");
    }
    return out;
  };

  const chips: [string, string, number][] = [
    ["All", "All", tabCounts.all],
    ["Active", "Active", tabCounts.active],
    ["Onboard", "Onboard", tabCounts.onboard],
    ["Inactive", "Inactive", tabCounts.inactive],
    ["Trash", "Trash", tabCounts.trash],
    ["PlanExpired", "Plan Expired", tabCounts.planExpired],
    ["NonCredit", "Non Credit Account", tabCounts.nonCredit],
  ];

  const allChecked = userList.length > 0 && selectedUsers.length === userList.length;
  const firstRow = totalUsers ? (page - 1) * pageSize + 1 : 0;
  const lastRow = totalUsers ? (page - 1) * pageSize + userList.length : 0;

  const menuItem = (icon: string, label: string, fn: () => void, danger = false) => (
    <button type="button" className={danger ? "dang" : ""} onClick={() => { setRowMenu(null); fn(); }}>
      <i className={icon}></i>{label}
    </button>
  );

  const openStatic = (user: any, modal: string) => { setSelectedUser(user); setStaticModal(modal); };

  // single-row status / trash actions → real /user/handleBulkAction after confirmation
  const confirmRowAction = (user: any, code: string) => {
    const name = user.user_companyName || `${user.user_first_name} ${user.user_last_name}`;
    const run = () => { setConfirmAction(null); handleApplyBulkAction(code, [user._id], true); };
    const actions: Record<string, ConfirmAction> = {
      "1": { icon: "ri-play-circle-line", tone: "ok", title: `Activate ${name}?`, body: "The workspace goes live again.", confirmLabel: "Activate", onConfirm: run },
      "2": { icon: "ri-pause-circle-line", tone: "warn", title: `Deactivate ${name}?`, body: "Broadcasts, bots and API calls stop right away.", confirmLabel: "Deactivate", danger: true, onConfirm: run },
      "3": { icon: "ri-delete-bin-line", tone: "bad", title: `Move ${name} to trash?`, body: "It can be restored later from the Trash tab.", confirmLabel: "Move to trash", danger: true, onConfirm: run },
      "5": { icon: "ri-arrow-go-back-line", tone: "ok", title: `Restore ${name}?`, body: "The customer moves back out of the trash.", confirmLabel: "Restore", onConfirm: run },
      "6": { icon: "ri-delete-bin-line", tone: "bad", title: `Permanently delete ${name}?`, body: "This cannot be undone.", confirmLabel: "Delete permanently", danger: true, onConfirm: run },
    };
    setConfirmAction(actions[code]);
  };

  const mu = rowMenu?.user;

  return (
    <>
      {isLoader && <Loader />}
      <div className="container-fluid">
        <div className="cx">
          {/* --- Page header --- */}
          <div className="cx-hdr">
            <div>
              <ul className="cx-crumb"><li>Master Admin</li><li>Customers</li></ul>
              <h1 className="cx-title">All Customers &amp; Users</h1>
              <p className="cx-sub">Every business on the platform — their plan, usage and account health.</p>
            </div>
            <div className="cx-actions">
              <div className="cx-pf">
                <select className="cx-select" value={dateFilter} onChange={(e) => handleDateFilterChange(e.target.value)}>
                  {DATE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {dateFilter === "custom" ? (
                  <span className="dr custom">
                    <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} aria-label="From date" />
                    –
                    <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} aria-label="To date" />
                  </span>
                ) : (
                  <span className="dr"><i className="ri-calendar-2-line"></i>{displayDateRange}</span>
                )}
              </div>
              <button type="button" className="cx-btn cx-btn-light" onClick={exportCustomers}
                title={`Export all ${totalUsers.toLocaleString("en-IN")} customers in this tab${searchQuery ? " matching the search" : ""}`}>
                <i className="ri-download-2-line"></i>Export{totalUsers ? ` (${totalUsers.toLocaleString("en-IN")})` : ""}
              </button>
            </div>
          </div>

          {/* --- Stats strip --- */}
          <div className="cx-st">
            <StatCell label="Total Users" value={stats.totalUsers.value} icon="ri-group-line" growth={stats.totalUsers.growth} />
            <StatCell label="Active Users" value={stats.activeUsers.value} icon="ri-user-follow-line" growth={stats.activeUsers.growth} />
            <StatCell label="Onboarding" value={stats.onboardUsers.value} icon="ri-user-add-line" growth={stats.onboardUsers.growth} />
            <StatCell label="Blocked Users" value="—" icon="ri-user-forbid-line" />
            <StatCell label="Inactive Users" value={stats.offboardUsers.value} icon="ri-user-unfollow-line" growth={stats.offboardUsers.growth} invert />
          </div>

          {/* --- Table card --- */}
          <div className="cx-card">
            <div className="cx-toolbar">
              <div className="cx-chips">
                {chips.map(([k, l, n]) => (
                  <button type="button" key={k} className={`cx-chip ${viewFilter === k ? "on" : ""}`} onClick={() => changeView(k)}>
                    {l} <b>{n || 0}</b>
                  </button>
                ))}
              </div>
              <div className="sp"></div>
              <input
                className="cx-input"
                type="search"
                placeholder="Search name, email, company, ID…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <select
                className="cx-select"
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
              >
                <option value="newest">Sort: Newest</option>
                <option value="oldest">Sort: Oldest</option>
              </select>
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
                <i className="ri-refresh-line"></i>Reset
              </button>
            </div>

            {selectedUsers.length > 0 && (
              <div className="cx-bulk">
                <b>{selectedUsers.length}</b> selected
                <div className="sp"></div>
                {viewFilter === "Trash" ? (
                  <>
                    <button type="button" onClick={() => handleApplyBulkAction("5")}><i className="ri-arrow-go-back-line"></i>Restore</button>
                    <button type="button" className="dang" onClick={() => handleApplyBulkAction("6")}><i className="ri-delete-bin-line"></i>Delete permanently</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => handleApplyBulkAction("1")}><i className="ri-play-circle-line"></i>Activate</button>
                    <button type="button" onClick={() => handleApplyBulkAction("2")}><i className="ri-pause-circle-line"></i>Deactivate</button>
                    <button type="button" className="dang" onClick={() => handleApplyBulkAction("3")}><i className="ri-delete-bin-line"></i>Move to trash</button>
                  </>
                )}
              </div>
            )}

            <div className="cx-tbl-wrap">
              <table className="cx-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 20 }}>
                      <input type="checkbox" onChange={handleSelectAll} checked={allChecked} />
                    </th>
                    <th>Customer</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th className="c">OS</th>
                    <th className="c">Wallet</th>
                    <th>Credit</th>
                    <th className="c">Free msgs</th>
                    <th>Renewal</th>
                    <th>Affiliate</th>
                    <th>Joined</th>
                    <th className="r">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userList && userList.length > 0 ? (
                    userList.map((item) => {
                      const isExpired = isSubscriptionExpired(item.user_plan_end_date);
                      const [sc, sl] = STATUS_PILL[item.user_status] || ["none", "Unknown"];
                      const waba = !!item.user_whatsapp?.isWabaConnected;
                      const credit = !!item.user_is_credit_line_enabled;
                      // user_affiliate_id is populated by /user/userList (first name, last name, company)
                      const affiliate = item.user_affiliate_id;
                      const affiliateName = typeof affiliate === "object" && affiliate
                        ? `${affiliate.affiliate_first_name || ""} ${affiliate.affiliate_last_name || ""}`.trim()
                        : "";
                      return (
                        <tr key={item._id} className="click" onClick={() => navigate(`/customers-and-users/view-user/${item._id}`)}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(item._id)}
                              onChange={() => handleSelectUser(item._id)}
                            />
                          </td>
                          <td>
                            <div className="cx-who">
                              <div className="av">{getInitials(item.user_companyName, item.user_companyName)}</div>
                              <div>
                                <b>{item.user_companyName || "—"}</b>
                                <small>
                                  <>{item.user_first_name} {item.user_last_name} · </>
                                  <span className="mono"> #{item.user_unique_id}</span>
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {item.user_plan_name || <span className="muted">—</span>}
                            {item.user_plan_type && <div className="fs12 muted">{item.user_plan_type}</div>}
                          </td>
                          <td><span className={`cx-pill ${sc}`}><i></i>{sl}</span></td>
                          <td className="c">
                            <span className={`cx-si ${waba ? "ok" : "bad"}`} data-tip={waba ? "WABA connected" : "WABA not connected"}>
                              <i className={waba ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}></i>
                            </span>
                          </td>
                          <td className={`c mono ${credit && Number(item.user_wallet_balance || 0) < 500 ? "down" : ""}`}>
                            {credit ? inr(item.user_wallet_balance) : <span className="muted">—</span>}
                          </td>
                          <td>
                            {(() => {
                              const [tone, icon, tip] = creditLineState(item);
                              return <span className={`cx-si ${tone}`} data-tip={tip}><i className={icon}></i></span>;
                            })()}
                          </td>
                          <td className="c fs12 mono" data-tip={item.serviceMessageUsage ? `${item.serviceMessageUsage.remaining} of ${item.serviceMessageUsage.limit} free messages left this month` : undefined}>
                            {item.serviceMessageUsage ? (
                              <span className={item.serviceMessageUsage.remaining === 0 ? "down" : ""}>
                                {item.serviceMessageUsage.count.toLocaleString()} / {item.serviceMessageUsage.limit.toLocaleString()}
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td className={`fs12 ${isExpired ? "down" : "muted"}`} style={isExpired ? { fontWeight: 600 } : undefined}>
                            {item.user_plan_end_date ? moment(item.user_plan_end_date).format("DD MMM YYYY") : "—"}
                            {isExpired && <div style={{ fontSize: 11, fontWeight: 500 }}>Expired</div>}
                          </td>
                          <td>
                            {affiliate ? (
                              <span className="cx-pill ok">
                                {affiliateName || "Assigned"}
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td className="fs12 muted">
                            {item.createdAt ? moment(item.createdAt).format("DD MMM YYYY") : "—"}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="cx-ra">
                              <button type="button" className="v" title="View" onClick={() => navigate(`/customers-and-users/view-user/${item._id}`)}>
                                <i className="ri-eye-line"></i>
                              </button>
                              <button type="button" title="More actions" onClick={(e) => openRowMenu(e, item)}>
                                <i className="ri-more-2-fill"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12}>
                        <div className="cx-empty">
                          <i className="ri-search-eye-line"></i>
                          No customers match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="cx-tfoot">
              <span>Showing <b>{firstRow}–{lastRow}</b> of <b>{(totalUsers || 0).toLocaleString("en-IN")}</b></span>
              <div className="cx-legend">
                <b>OS</b>
                <span><span className="cx-si ok"><i className="ri-checkbox-circle-fill"></i></span>WABA connected</span>
                <span><span className="cx-si bad"><i className="ri-error-warning-fill"></i></span>WABA pending</span>
                <b style={{ marginLeft: 6 }}>Credit</b>
                <span><span className="cx-si ok"><i className="ri-bank-card-2-fill"></i></span>Enabled</span>
                <span><span className="cx-si none"><i className="ri-subtract-line"></i></span>Off</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span>Rows</span>
                <select className="cx-select" value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="cx-pager">
                  <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                  {pageItems().map((p, i) =>
                    typeof p === "number" ? (
                      <button type="button" key={p} className={p === page ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                    ) : (
                      <span key={`gap-${i}`} className="gap">…</span>
                    )
                  )}
                  <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row action menu — real actions + static design actions (marked "static") */}
      {rowMenu && mu && (
        <div className="cx-rmenu" ref={menuRef} style={{ top: rowMenu.top, left: rowMenu.left }}>
          <div className="hd">{mu.user_first_name} {mu.user_last_name}{mu.user_companyName ? ` · ${mu.user_companyName}` : ""}</div>
          {menuItem("ri-eye-line", "View details", () => navigate(`/customers-and-users/view-user/${mu._id}`))}
          {menuItem("ri-login-circle-line", "Login as user", () => handleDirectLogin(mu._id, mu.user_is_credit_line_enabled || false))}
          {menuItem("ri-edit-line", "Edit customer", () => openStatic(mu, "edit"))/* static */}
          <hr />
          {menuItem("ri-exchange-line", "Change plan", () => { setSelectedUser(mu); setShowPlanModal(true); })}
          {menuItem("ri-time-line", "Extend validity", () => openStatic(mu, "extend"))/* static */}
          {menuItem("ri-coins-line", "Adjust wallet", () => (mu.user_is_credit_line_enabled
            ? (setSelectedUser(mu), setShowCreditModal(true))
            : openStatic(mu, "wallet")))/* real for credit-line customers, static otherwise */}
          {menuItem("ri-bank-card-2-line", "Credit line", () => openStatic(mu, "credit"))/* static */}
          {mu.user_is_credit_line_enabled && menuItem("ri-refresh-line", "Reconcile wallet", () => handleReconcileWallet(mu._id))}
          {menuItem("ri-toggle-line", "Feature overrides", () => openStatic(mu, "overrides"))/* static */}
          {menuItem("ri-shield-user-line", "Assign affiliate", () => { setSelectedUser(mu); setShowAssignAffiliateModal(true); })}
          {(mu.user_whatsapp?.isWabaConnected || mu.user_is_credit_line_enabled) && <hr />}
          {mu.user_whatsapp?.isWabaConnected && menuItem("ri-whatsapp-line", "WABA details", () => { setSelectedUser(mu); handleOpenModal(mu._id); })}
          {mu.user_is_credit_line_enabled && menuItem("ri-price-tag-3-line", "Manage Meta pricing", () => navigate(`/meta-pricing/${mu._id}`))}
          <hr />
          {menuItem("ri-lock-password-line", "Reset password", () => toast.info(`Password reset link to ${mu.user_email} · demo only, not sent`))/* static */}
          {menuItem("ri-mail-send-line", "Send email", () => openStatic(mu, "email"))/* static */}
          {menuItem("ri-file-list-3-line", "View invoices", () => navigate("/invoices"))/* static page */}
          <hr />
          {mu.user_status === 2 ? (
            <>
              {menuItem("ri-arrow-go-back-line", "Restore", () => confirmRowAction(mu, "5"))}
              {menuItem("ri-delete-bin-line", "Delete permanently", () => confirmRowAction(mu, "6"), true)}
            </>
          ) : (
            <>
              {mu.user_status === 1
                ? menuItem("ri-pause-circle-line", "Deactivate", () => confirmRowAction(mu, "2"), true)
                : menuItem("ri-play-circle-line", "Activate", () => confirmRowAction(mu, "1"))}
              {menuItem("ri-delete-bin-line", "Move to trash", () => confirmRowAction(mu, "3"), true)}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <AssignPlanModal show={showPlanModal} onHide={() => setShowPlanModal(false)} user={selectedUser} onSuccess={() => getUserList()} />
      <AssignAffiliateModal show={showAssignAffiliateModal} onHide={() => { getUserList(); setShowAssignAffiliateModal(false); }} userData={selectedUser} />
      <WabaDetailsModal show={showWabaModal} onHide={() => setShowWabaModal(false)} userID={selectedUserId} initialData={selectedUser?.user_meta_account_info || {}} userData={selectedUser || {}} onSyncComplete={getUserList} />
      <ManageCreditsModal show={showCreditModal} onHide={() => setShowCreditModal(false)} user={selectedUser} onSuccess={() => getUserList()} />
      <ConfirmActionModal action={confirmAction} onHide={() => setConfirmAction(null)} />

      {/* Static design modals */}
      <AdjustWalletModal show={staticModal === "wallet"} onHide={() => setStaticModal(null)} user={selectedUser} />
      <CreditLineModal show={staticModal === "credit"} onHide={() => setStaticModal(null)} user={selectedUser} />
      <ExtendValidityModal show={staticModal === "extend"} onHide={() => setStaticModal(null)} user={selectedUser} />
      <SendEmailModal show={staticModal === "email"} onHide={() => setStaticModal(null)} user={selectedUser} />
      <FeatureOverridesModal show={staticModal === "overrides"} onHide={() => setStaticModal(null)} user={selectedUser} />
      <div className="cx"><EditCustomerDrawer show={staticModal === "edit"} onClose={() => setStaticModal(null)} user={selectedUser} /></div>
    </>
  );
}

export default UsersNew;
